import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { apiClient } from '@/api/axiosClient';
import SearchableSelect from '@/components/common/SearchableSelect';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { STOCK_STATUSES } from '@/types/adjustments';
import type { StockStatusBalanceDto, StockStatusChangeRequest, StockStatusLabelDto } from '@/types/adjustments';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Moves quantity between stock statuses. Deliberately not a Draft/Commit document like the
 * adjustment behind it — nothing physically moves and no quantity changes, so the server posts the
 * pair of ledger rows immediately (see StockStatusChangeService).
 *
 * Two modes, matching Process Configuration exactly as the Stock Adjustment screen behind it does:
 *  - Barcode Required OFF: pick a stock row, type a quantity.
 *  - Barcode Required ON:  pick a stock row, then tick the individual physical units inside it.
 *    Quantity becomes the sum of the ticked labels and is not editable — the server recomputes it
 *    from the same labels and ignores whatever the client sent, so this is display, not enforcement.
 */
export default function StockStatusChangeDialog({ open, onClose }: Props) {
  const { t } = useTranslation('stockAdjustment');
  const queryClient = useQueryClient();

  const { data: processConfig } = useProcessConfiguration();
  const barcodeMandatory = processConfig?.barcodeMandatory ?? false;

  const { data: balances = [], isLoading } = useQuery({
    queryKey: ['stock-status-balances'],
    queryFn: async () => (await apiClient.get<StockStatusBalanceDto[]>('/stock-status-changes/balances')).data,
    enabled: open,
  });

  const [rowKey, setRowKey] = useState('');
  const [toStatus, setToStatus] = useState('Available');
  const [qty, setQty] = useState<number>(0);
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selectedBarcodes, setSelectedBarcodes] = useState<string[]>([]);

  // Only rows with something actually movable are worth offering — a row fully reserved for an open
  // pick list would just fail server-side.
  const selectable = useMemo(() => balances.filter((b) => b.movableQty > 0), [balances]);

  const keyOf = (b: StockStatusBalanceDto) =>
    [b.materialId, b.warehouseId, b.locationId ?? '', b.batchId ?? '', b.clientId ?? '', b.stockStatus].join('|');

  const selected = selectable.find((b) => keyOf(b) === rowKey) ?? null;

  // The physical units inside the chosen row, in its current status. Only fetched in barcode mode —
  // row-wise has no use for them.
  const { data: units = [], isFetching: unitsLoading } = useQuery({
    queryKey: ['stock-status-labels', rowKey],
    queryFn: async () => (await apiClient.get<StockStatusLabelDto[]>('/stock-status-changes/labels', {
      params: {
        materialId: selected!.materialId,
        warehouseId: selected!.warehouseId,
        locationId: selected!.locationId,
        batchId: selected!.batchId,
        fromStatus: selected!.stockStatus,
      },
    })).data,
    enabled: open && barcodeMandatory && selected !== null,
  });

  // Barcode mode: the ticked labels ARE the quantity, so it is never typed.
  const selectedQty = useMemo(
    () => units.filter((u) => selectedBarcodes.includes(u.barcode)).reduce((sum, u) => sum + u.qty, 0),
    [units, selectedBarcodes],
  );
  const effectiveQty = barcodeMandatory ? selectedQty : qty;

  useEffect(() => {
    if (!open) {
      setRowKey('');
      setToStatus('Available');
      setQty(0);
      setRemarks('');
      setError(null);
      setSelectedBarcodes([]);
    }
  }, [open]);

  // Default the target to something other than where the stock already is, so the form never opens
  // in a state the server would reject.
  useEffect(() => {
    if (selected && selected.stockStatus === toStatus) {
      setToStatus(STOCK_STATUSES.find((s) => s !== selected.stockStatus) ?? 'Quarantine');
    }
  }, [selected, toStatus]);

  const toggleBarcode = (barcode: string) => {
    setError(null);
    setSelectedBarcodes((prev) => (prev.includes(barcode) ? prev.filter((b) => b !== barcode) : [...prev, barcode]));
  };

  const allSelected = units.length > 0 && selectedBarcodes.length === units.length;

  const mutation = useMutation({
    mutationFn: () => {
      if (!selected) throw new Error('No stock selected.');
      const payload: StockStatusChangeRequest = {
        materialId: selected.materialId,
        warehouseId: selected.warehouseId,
        locationId: selected.locationId,
        batchId: selected.batchId,
        clientId: selected.clientId,
        fromStatus: selected.stockStatus,
        toStatus,
        qty: effectiveQty,
        remarks: remarks || null,
        barcodes: barcodeMandatory ? selectedBarcodes : null,
      };
      return apiClient.post('/stock-status-changes', payload);
    },
    onSuccess: () => {
      // On-hand by status changed, so anything reading stock is now stale.
      queryClient.invalidateQueries({ queryKey: ['stock-status-balances'] });
      queryClient.invalidateQueries({ queryKey: ['stock-status-labels'] });
      queryClient.invalidateQueries({ queryKey: ['stock-adjustments'] });
      onClose();
    },
    onError: (e: unknown) => {
      const message =
        (e as { response?: { data?: { errors?: string[]; message?: string } } })?.response?.data?.errors?.[0]
        ?? (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Could not change the stock status.';
      setError(message);
    },
  });

  const overMovable = selected !== null && effectiveQty > selected.movableQty;
  const canSubmit =
    selected !== null && effectiveQty > 0 && !overMovable
    && toStatus !== selected.stockStatus && !mutation.isPending
    && (!barcodeMandatory || selectedBarcodes.length > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('statusChange.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText variant="body2" sx={{ mb: 2 }}>
          {t('statusChange.subtitle')}
        </DialogContentText>

        {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert> : undefined}

        {!isLoading && selectable.length === 0 ? (
          <Alert severity="info">{t('statusChange.noStock')}</Alert>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SearchableSelect
              label={t('statusChange.sourceRow')}
              value={rowKey}
              onChange={(v) => { setRowKey(v); setQty(0); setSelectedBarcodes([]); setError(null); }}
              fullWidth
              options={selectable.map((b) => ({
                value: keyOf(b),
                label: t('statusChange.rowLabel', {
                  materialCode: b.materialCode,
                  status: b.stockStatus,
                  location: b.locationCode ?? t('statusChange.noLocation'),
                  movable: b.movableQty,
                }),
              }))}
            />

            {/* Barcode-wise: the units inside the chosen row. Ticking them is what sets the quantity. */}
            {barcodeMandatory && selected ? (
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle2">{t('statusChange.unitsTitle')}</Typography>
                  {units.length > 0 ? (
                    <Button
                      size="small"
                      onClick={() => setSelectedBarcodes(allSelected ? [] : units.map((u) => u.barcode))}
                    >
                      {allSelected ? t('statusChange.clearAll') : t('statusChange.selectAll')}
                    </Button>
                  ) : undefined}
                </Stack>

                {unitsLoading ? <LinearProgress /> : undefined}

                {!unitsLoading && units.length === 0 ? (
                  // Stock exists on the row but no labels do — pre-barcode stock, or labels never
                  // printed for it. Saying so beats an empty box with no explanation.
                  <Alert severity="warning">{t('statusChange.noUnits')}</Alert>
                ) : undefined}

                {units.length > 0 ? (
                  <Box sx={{ maxHeight: 220, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 0.5 }}>
                    {units.map((u) => (
                      <Box key={u.barcode}>
                        <FormControlLabel
                          control={<Checkbox size="small" checked={selectedBarcodes.includes(u.barcode)} onChange={() => toggleBarcode(u.barcode)} />}
                          label={
                            <Box>
                              <Typography variant="body2">{u.barcode}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t('statusChange.unitMeta', {
                                  qty: u.qty,
                                  source: u.source,
                                  batch: u.batchNo ?? '—',
                                })}
                              </Typography>
                            </Box>
                          }
                          sx={{ py: 0.25, alignItems: 'center' }}
                        />
                        <Divider />
                      </Box>
                    ))}
                  </Box>
                ) : undefined}
              </Box>
            ) : undefined}

            <TextField
              select
              label={t('statusChange.toStatus')}
              value={toStatus}
              onChange={(e) => setToStatus(e.target.value)}
              fullWidth
              disabled={!selected}
            >
              {STOCK_STATUSES.filter((s) => s !== selected?.stockStatus).map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>

            <TextField
              type="number"
              label={t('statusChange.qty')}
              value={effectiveQty}
              onChange={(e) => setQty(Number(e.target.value))}
              fullWidth
              error={overMovable}
              // Barcode-wise the figure is derived from the ticked units, so typing over it would
              // only create a number the server would discard.
              disabled={barcodeMandatory || !selected}
              helperText={
                barcodeMandatory
                  ? t('statusChange.qtyFromUnits', { count: selectedBarcodes.length })
                  : selected ? t('statusChange.movable', { qty: selected.movableQty }) : t('statusChange.sourceRowHelp')
              }
            />

            <TextField
              label={t('statusChange.remarks')}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => { setError(null); mutation.mutate(); }}>
          {t('statusChange.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
